import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Package, Plus, X, Save, Search, Trash2, Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { isAdminUser } from "../lib/roles";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const MAX_SLOTS = 5;

/**
 * BundlesManager — Super Admin section for the 5 Dynamic Bundles.
 * Each slot is renamable, can hold multiple products, has a bundle price and
 * an active flag. Products are picked from a searchable typeahead popover.
 * Regular admins see a read-only overview; personnel/clients see nothing.
 */
export default function BundlesManager() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const canView = isAdminUser(user); // admin + super_admin
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null); // { bundle | null, slotIndex }
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [clientQ, setClientQ] = useState("");

  const load = async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [b, p, c] = await Promise.all([
        api.get("/bundles").then((r) => r.data),
        api.get("/products").then((r) => r.data),
        api.get("/clients").then((r) => r.data).catch(() => []),
      ]);
      setBundles(b);
      setProducts(p);
      setClients(c);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  // Fill up to 5 slots — extra empty placeholders on the right.
  const slots = useMemo(() => {
    const out = [...bundles.slice(0, MAX_SLOTS)];
    while (out.length < MAX_SLOTS) out.push(null);
    return out;
  }, [bundles]);

  const openSlot = (bundle, i) => {
    setEditingSlot({ bundle, slotIndex: i });
    setForm(
      bundle
        ? {
            id: bundle.id,
            name: bundle.name || `Package Deal #${i + 1}`,
            product_ids: bundle.product_ids || [],
            bundle_price: bundle.bundle_price || 0,
            active: bundle.active !== false,
            sort_order: bundle.sort_order ?? i,
            visibility: bundle.visibility || "public",
            assigned_client_ids: bundle.assigned_client_ids || [],
          }
        : {
            name: `Package Deal #${i + 1}`,
            product_ids: [],
            bundle_price: 0,
            active: true,
            sort_order: i,
            visibility: "public",
            assigned_client_ids: [],
          }
    );
    setQ("");
    setClientQ("");
  };

  const closeSlot = () => {
    setEditingSlot(null);
    setForm(null);
    setQ("");
  };

  const save = async () => {
    if (!form) return;
    if (!form.name?.trim()) {
      toast.error("Bundle needs a name");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        product_ids: form.product_ids || [],
        bundle_price: Number(form.bundle_price) || 0,
        active: !!form.active,
        sort_order: form.sort_order,
        visibility: form.visibility || "public",
        assigned_client_ids: form.visibility === "assigned" ? (form.assigned_client_ids || []) : [],
      };
      if (form.id) {
        await api.put(`/bundles/${form.id}`, payload);
        toast.success("Bundle saved");
      } else {
        await api.post("/bundles", payload);
        toast.success("Bundle created");
      }
      closeSlot();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form?.id) return;
    if (!window.confirm(`Delete bundle "${form.name}"?`)) return;
    try {
      await api.delete(`/bundles/${form.id}`);
      toast.success("Bundle deleted");
      closeSlot();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const toggleProduct = (pid) => {
    setForm((f) => {
      const cur = f.product_ids || [];
      return {
        ...f,
        product_ids: cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid],
      };
    });
  };

  const toggleAssignedClient = (cid) => {
    setForm((f) => {
      const cur = f.assigned_client_ids || [];
      return {
        ...f,
        assigned_client_ids: cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid],
      };
    });
  };

  const filteredClients = useMemo(() => {
    const term = (clientQ || "").trim().toLowerCase();
    if (!term) return clients.slice(0, 20);
    return clients
      .filter((c) =>
        (c.name || "").toLowerCase().includes(term) ||
        (c.company || "").toLowerCase().includes(term) ||
        (c.slug || "").toLowerCase().includes(term) ||
        (c.email || "").toLowerCase().includes(term)
      )
      .slice(0, 40);
  }, [clientQ, clients]);

  const filteredProducts = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 30);
    return products
      .filter((p) => (p.name || "").toLowerCase().includes(term) || (p.sku || "").toLowerCase().includes(term))
      .slice(0, 60);
  }, [q, products]);

  const originalTotal = useMemo(() => {
    if (!form) return 0;
    return (form.product_ids || []).reduce((s, pid) => {
      const p = products.find((x) => x.id === pid);
      return s + Number(p?.retail_price || 0);
    }, 0);
  }, [form, products]);

  if (!canView) return null;

  return (
    <Card className="p-5 mt-4" data-testid="bundles-manager-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
          <Package size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow">Dynamic Bundles · Package Deals</p>
          <p className="text-xs text-gray-500">
            Configure up to 5 curated bundles. Active bundles power the shop ticker and upsell overlays.
          </p>
        </div>
        {!isSuperAdmin && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-amber-800 bg-amber-100 border border-amber-200 px-2 py-1 rounded"
            data-testid="bundles-readonly-badge"
            title="Only Super Admins can edit bundles"
          >
            <Lock size={11} /> View only
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-gray-400 text-sm py-6 text-center">Loading bundles…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {slots.map((b, i) => (
            <button
              key={b?.id || `slot-${i}`}
              onClick={() => {
                if (!isSuperAdmin) {
                  toast.info("Super Admin only", { description: "Ask the app owner to update bundles." });
                  return;
                }
                openSlot(b, i);
              }}
              className={`text-left rounded-lg border p-3 transition-all ${
                isSuperAdmin ? "hover:shadow-md hover:border-amber-400 cursor-pointer" : "cursor-not-allowed opacity-90"
              } ${
                b ? "border-amber-300 bg-amber-50/40" : "border-dashed border-neutral-300 bg-white"
              }`}
              data-testid={`bundle-slot-${i}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-neutral-500">Slot {i + 1}</span>
                {b && (
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-widest ${
                        (b.visibility || "public") === "public"        ? "bg-blue-100 text-blue-800" :
                        (b.visibility || "public") === "clients_only"  ? "bg-purple-100 text-purple-800" :
                                                                          "bg-emerald-100 text-emerald-800"
                      }`}
                      title={
                        (b.visibility || "public") === "public" ? "Visible on the main /shop" :
                        (b.visibility || "public") === "clients_only" ? "Visible on every client sub-portal" :
                        `Assigned to ${(b.assigned_client_ids || []).length} client(s)`
                      }
                    >
                      {(b.visibility || "public") === "public" ? "public"
                        : (b.visibility || "public") === "clients_only" ? "clients"
                        : `assigned·${(b.assigned_client_ids || []).length}`}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        b.active === false ? "bg-neutral-200 text-neutral-600" : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {b.active === false ? "off" : "live"}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-1 truncate text-sm font-semibold text-neutral-900">
                {b ? b.name : "+ Configure bundle"}
              </div>
              {b ? (
                <>
                  <div className="mt-2 text-lg font-bold tabular-nums text-amber-700">
                    ${Number(b.bundle_price || 0).toFixed(2)}
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    {b.products?.length || 0} item{b.products?.length === 1 ? "" : "s"}
                    {Number(b.savings || 0) > 0 && (
                      <span className="ml-1 text-emerald-700">· save ${Number(b.savings).toFixed(2)}</span>
                    )}
                  </div>
                </>
              ) : (
                <div className="mt-4 text-xs text-neutral-400">Click to create</div>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!editingSlot} onOpenChange={(o) => !o && closeSlot()}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bundle-edit-dialog">
          <DialogHeader>
            <DialogTitle>
              {form?.id ? "Edit bundle" : "Create bundle"} · Slot {(editingSlot?.slotIndex ?? 0) + 1}
            </DialogTitle>
          </DialogHeader>
          {form && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Bundle name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Curl Care Combo"
                    data-testid="bundle-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Bundle price ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={form.bundle_price}
                    onChange={(e) => setForm({ ...form, bundle_price: e.target.value })}
                    data-testid="bundle-price-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(e) => setForm({ ...form, active: e.target.checked })}
                    data-testid="bundle-active-toggle"
                  />
                  <span>Active on storefront</span>
                </label>
                <span className="text-neutral-400">·</span>
                <span className="text-neutral-500 tabular-nums">
                  Products retail total: ${originalTotal.toFixed(2)}
                </span>
                {Number(form.bundle_price) > 0 && originalTotal > Number(form.bundle_price) && (
                  <span className="text-emerald-700 font-medium">
                    · save ${(originalTotal - Number(form.bundle_price)).toFixed(2)}
                  </span>
                )}
              </div>

              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 space-y-2">
                <div>
                  <Label className="text-xs">Visibility</Label>
                  <Select
                    value={form.visibility || "public"}
                    onValueChange={(v) => setForm({ ...form, visibility: v })}
                  >
                    <SelectTrigger className="bg-white" data-testid="bundle-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public · main /shop storefront</SelectItem>
                      <SelectItem value="clients_only">Clients Only · every client sub-portal</SelectItem>
                      <SelectItem value="assigned">Assigned · only specific clients I pick</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-[11px] text-neutral-500">
                    {form.visibility === "public" && "Visible on the public retail store to every visitor."}
                    {form.visibility === "clients_only" && "Hidden from /shop; visible on every client sub-portal at /shop/<slug>."}
                    {form.visibility === "assigned" && "Hidden everywhere except the sub-portals of the clients you select below."}
                  </p>
                </div>

                {form.visibility === "assigned" && (
                  <div>
                    <Label className="text-xs">Assigned clients ({(form.assigned_client_ids || []).length})</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5 mb-2">
                      {(form.assigned_client_ids || []).map((cid) => {
                        const c = clients.find((x) => x.id === cid);
                        if (!c) return null;
                        return (
                          <span key={cid} className="inline-flex items-center gap-1 rounded-full bg-white border border-emerald-300 px-2 py-0.5 text-xs">
                            <span className="truncate max-w-[180px] capitalize">{c.name || c.company || c.slug}</span>
                            <button
                              onClick={() => toggleAssignedClient(cid)}
                              className="text-neutral-400 hover:text-red-600"
                              data-testid={`bundle-remove-client-${cid}`}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })}
                      {(form.assigned_client_ids || []).length === 0 && (
                        <p className="text-xs text-neutral-400 italic">No clients selected yet.</p>
                      )}
                    </div>
                    <div className="relative">
                      <Search size={12} className="absolute left-2.5 top-2.5 text-gray-400" />
                      <Input
                        className="pl-7 bg-white"
                        placeholder="Search clients by name, company, or slug…"
                        value={clientQ}
                        onChange={(e) => setClientQ(e.target.value)}
                        data-testid="bundle-client-search"
                      />
                    </div>
                    <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-neutral-200 bg-white">
                      {filteredClients.map((c) => {
                        const selected = (form.assigned_client_ids || []).includes(c.id);
                        return (
                          <button
                            key={c.id}
                            onClick={() => toggleAssignedClient(c.id)}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs border-b last:border-b-0 border-neutral-100 hover:bg-emerald-50 ${
                              selected ? "bg-emerald-50" : ""
                            }`}
                            data-testid={`bundle-toggle-client-${c.id}`}
                          >
                            <input type="checkbox" readOnly checked={selected} className="pointer-events-none" />
                            <span className="flex-1 truncate capitalize font-medium">{c.name || c.company}</span>
                            <span className="font-mono text-[10px] text-neutral-400 truncate max-w-[120px]">/shop/{c.slug}</span>
                          </button>
                        );
                      })}
                      {filteredClients.length === 0 && (
                        <p className="p-3 text-center text-xs text-neutral-400">No clients match.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-xs">Selected items ({(form.product_ids || []).length})</Label>
                <div className="mt-1 min-h-[40px] rounded-lg border border-neutral-200 bg-neutral-50 p-2">
                  {(form.product_ids || []).length === 0 ? (
                    <p className="text-xs text-neutral-400">Nothing selected yet — pick from the list below.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {form.product_ids.map((pid) => {
                        const p = products.find((x) => x.id === pid);
                        if (!p) return null;
                        return (
                          <span
                            key={pid}
                            className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-300 px-2 py-0.5 text-xs"
                          >
                            <span className="truncate max-w-[180px] capitalize">{p.name}</span>
                            <button
                              onClick={() => toggleProduct(pid)}
                              className="text-neutral-400 hover:text-red-600"
                              data-testid={`bundle-remove-product-${pid}`}
                            >
                              <X size={12} />
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs">Add products</Label>
                <div className="relative">
                  <Search size={12} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <Input
                    className="pl-7"
                    placeholder="Search products by name or SKU…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    data-testid="bundle-product-search"
                  />
                </div>
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-neutral-200 bg-white">
                  {filteredProducts.map((p) => {
                    const selected = (form.product_ids || []).includes(p.id);
                    return (
                      <button
                        key={p.id}
                        onClick={() => toggleProduct(p.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-xs border-b last:border-b-0 border-neutral-100 hover:bg-amber-50 ${
                          selected ? "bg-amber-50" : ""
                        }`}
                        data-testid={`bundle-toggle-product-${p.id}`}
                      >
                        <input type="checkbox" readOnly checked={selected} className="pointer-events-none" />
                        <span className="flex-1 truncate capitalize font-medium">{p.name}</span>
                        <span className="font-mono text-[10px] text-neutral-400 truncate max-w-[120px]">{p.sku}</span>
                        <span className="tabular-nums text-neutral-500">${Number(p.retail_price || 0).toFixed(2)}</span>
                      </button>
                    );
                  })}
                  {filteredProducts.length === 0 && (
                    <p className="p-3 text-center text-xs text-neutral-400">No products match.</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 pt-2 border-t border-neutral-200">
                {form.id ? (
                  <Button
                    variant="outline"
                    onClick={remove}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="bundle-delete"
                  >
                    <Trash2 size={14} className="mr-1.5" /> Delete
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button variant="outline" onClick={closeSlot} data-testid="bundle-cancel">
                    Cancel
                  </Button>
                  <Button
                    className="btn-brand"
                    onClick={save}
                    disabled={saving}
                    data-testid="bundle-save"
                  >
                    <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save bundle"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
